import { SearchFocusTarget, User, UserRole } from "@/App";
import { useApp } from "@/contexts/AppContext";
import {
  Book,
  Menu,
  User as UserIcon,
  LogOut,
  Settings,
  BarChart3,
  FolderTree,
  Lightbulb,
  FileEdit,
  Moon,
  Sun,
  Sunrise,
  Sunset,
  Laptop,
  Palette,
  Sparkles,
  Globe,
  Minus,
  Plus,
  Search as SearchIcon,
  MessageSquare,
  Bug,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useProfilePictureSrc } from "@/utils/profilePicture";
import { useUnreadMessages } from "@/app/hooks/useUnreadMessages";
import { useUnreadTickets } from "@/app/hooks/useUnreadTickets";
import { UI_SCALE_MAX, UI_SCALE_MIN, toUiScaleFactor } from "@/utils/uiScale";

type NavTarget =
  | "home"
  | "categories"
  | "statistics"
  | "search"
  | "suggest-website"
  | "manage-websites"
  | "admin"
  | "account";

interface HeaderProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  currentPage?: string;
  samePageHandlers?: Partial<Record<NavTarget, () => void>>;
  onNavigateToSearchPage?: (target: SearchFocusTarget) => void;
}

/**
 * Header renders the primary navigation bar and user controls across the app.
 */
export function Header({
  navigate,
  currentUser,
  onLogout,
  currentPage,
  samePageHandlers,
  onNavigateToSearchPage,
}: HeaderProps) {
  const {
    t,
    language,
    setLanguage,
    activeTheme,
    setThemeMode,
    themePreferences,
    uiScale,
    increaseUiScale,
    decreaseUiScale,
  } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [appearanceMenuOpen, setAppearanceMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [languageMenuOffset, setLanguageMenuOffset] = useState({ x: 0, y: 0 });
  const [appearanceMenuOffset, setAppearanceMenuOffset] = useState({ x: 0, y: 0 });
  const [profileMenuOffset, setProfileMenuOffset] = useState({ x: 0, y: 0 });
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [registerHovered, setRegisterHovered] = useState(false);
  const headerRowRef = useRef<HTMLDivElement | null>(null);
  const requiredWidthRef = useRef(0);
  const layoutUpdateRafRef = useRef<number | null>(null);
  const [forceMobile, setForceMobile] = useState(false);
  const languageMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const appearanceMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const profileMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const {
    src: profilePictureSrc,
    onError: handleProfilePictureError,
  } = useProfilePictureSrc(currentUser?.profilePicture);
  const userInitials = currentUser?.username
    ? currentUser.username.substring(0, 2).toUpperCase()
    : "";
  const { unreadCount } = useUnreadMessages(Boolean(currentUser));
  const { unreadCount: unreadTicketCount } = useUnreadTickets(Boolean(currentUser));
  const showDesktop = !forceMobile;
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const brandIconStyle = {
    color: "var(--background)",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const navBaseColor = primaryTextStyle.color;
  const navHoverColor =
    "color-mix(in srgb, var(--primary) 82%, var(--foreground))";
  const navButtonStyle = (id: string) => ({
    color: hoveredNav === id ? navHoverColor : navBaseColor,
  });
  const guestThemeTone = activeTheme.tone === "dark" ? "dark" : "light";
  const handleGuestThemeToggle = () => {
    setThemeMode(guestThemeTone === "dark" ? "light" : "dark");
  };
  const guestThemeLabel =
    guestThemeTone === "dark"
      ? t("appearance.modeDark")
      : t("appearance.modeLight");
  const guestThemeIcon =
    guestThemeTone === "dark" ? (
      <Moon className="w-4 h-4 md:w-5 md:h-5" />
    ) : (
      <Sun className="w-4 h-4 md:w-5 md:h-5" />
    );
  const languageBadgeStyle = {
    backgroundColor: "color-mix(in srgb, var(--accent) 22%, var(--background))",
    color: "color-mix(in srgb, var(--foreground) 92%, var(--background))",
    border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--background))",
  } as const;
  const dropdownSurfaceStyle = {
    backgroundColor: "var(--surface)",
    borderColor: "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const brandIconWrapperStyle = {
    backgroundColor: "var(--primary)",
  } as const;
  const avatarStyle = {
    backgroundColor: "color-mix(in srgb, var(--foreground) 6%, var(--background))",
  } as const;
  const registerButtonStyle = {
    backgroundColor: registerHovered
      ? "color-mix(in srgb, var(--primary) 30%, var(--background))"
      : "color-mix(in srgb, var(--primary) 20%, var(--background))",
    borderColor: "color-mix(in srgb, var(--primary) 35%, var(--foreground))",
    color: navBaseColor,
  } as const;
  const dividerStyle = {
    borderColor: "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const messageIconStyle = {
    color:
      unreadCount > 0 ? "var(--primary)" : (primaryTextStyle.color as string),
  };
  const ticketIconStyle = {
    color:
      unreadTicketCount > 0
        ? "var(--primary)"
        : (primaryTextStyle.color as string),
  };
  const decreaseScaleDisabled = uiScale <= UI_SCALE_MIN;
  const increaseScaleDisabled = uiScale >= UI_SCALE_MAX;
  const uiScaleFactor = toUiScaleFactor(uiScale);

  const resolveMenuOffset = useCallback(
    (trigger: HTMLElement | null, content: HTMLElement | null) => {
      if (!trigger || !content) {
        return { x: 0, y: 0 };
      }
      const triggerRect = trigger.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const rawOffsetX = triggerRect.right - contentRect.right;
      const rawOffsetY = triggerRect.bottom - contentRect.top;
      if (!Number.isFinite(rawOffsetX) || !Number.isFinite(rawOffsetY)) {
        return { x: 0, y: 0 };
      }
      const divisor = uiScaleFactor > 0 ? uiScaleFactor : 1;
      return {
        x: rawOffsetX / divisor,
        y: rawOffsetY / divisor,
      };
    },
    [uiScaleFactor]
  );

  const measureLanguageMenuOffset = useCallback(() => {
    if (typeof window === "undefined") return;
    const trigger = languageMenuTriggerRef.current;
    const content = document.querySelector("[data-language-menu]");
    const offset =
      uiScale === 100 || !(content instanceof HTMLElement)
        ? { x: 0, y: 0 }
        : resolveMenuOffset(trigger, content);
    setLanguageMenuOffset(offset);
  }, [resolveMenuOffset, uiScale]);

  const measureAppearanceMenuOffset = useCallback(() => {
    if (typeof window === "undefined") return;
    const trigger = appearanceMenuTriggerRef.current;
    const content = document.querySelector("[data-appearance-menu]");
    const offset =
      uiScale === 100 || !(content instanceof HTMLElement)
        ? { x: 0, y: 0 }
        : resolveMenuOffset(trigger, content);
    setAppearanceMenuOffset(offset);
  }, [resolveMenuOffset, uiScale]);

  const measureProfileMenuOffset = useCallback(() => {
    if (typeof window === "undefined") return;
    const trigger = profileMenuTriggerRef.current;
    const content = document.querySelector("[data-profile-menu]");
    const offset =
      uiScale === 100 || !(content instanceof HTMLElement)
        ? { x: 0, y: 0 }
        : resolveMenuOffset(trigger, content);
    setProfileMenuOffset(offset);
  }, [resolveMenuOffset, uiScale]);

  const handleNavigate = (page: string) => {
    navigate(page);
    setMobileMenuOpen(false);
  };

  const handlePrimaryNav = (target: "home" | "categories" | "statistics") => {
    if (currentPage === target && samePageHandlers?.[target]) {
      samePageHandlers[target]!();
      setMobileMenuOpen(false);
      return;
    }

    if (target === "categories" && onNavigateToSearchPage) {
      onNavigateToSearchPage("categories");
      setMobileMenuOpen(false);
      return;
    }

    handleNavigate(target);
  };

  const handleSearchNav = () => {
    if (
      (currentPage === "categories" || currentPage === "search") &&
      samePageHandlers?.search
    ) {
      samePageHandlers.search();
      setMobileMenuOpen(false);
      return;
    }

    if (onNavigateToSearchPage) {
      onNavigateToSearchPage("search");
    } else {
      handleNavigate("search");
    }
    setMobileMenuOpen(false);
  };

  const handleTargetNav = (target: NavTarget) => {
    if (currentPage === target && samePageHandlers?.[target]) {
      samePageHandlers[target]!();
      setMobileMenuOpen(false);
      return;
    }
    handleNavigate(target);
  };

  const handleAppearanceNav = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("account-settings-tab", "appearance");
      window.dispatchEvent(new Event("account-settings-tab-change"));
    }
    handleTargetNav("account");
  };

  const handleMessagesNav = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("account-settings-tab", "messages");
      window.dispatchEvent(new Event("account-settings-tab-change"));
    }
    handleTargetNav("account");
  };

  const handleTicketsNav = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("account-settings-tab", "tickets");
      window.dispatchEvent(new Event("account-settings-tab-change"));
    }
    handleTargetNav("account");
  };

  // Track when the header content no longer fits to toggle mobile layout.
  const updateLayoutMode = useCallback(() => {
    const row = headerRowRef.current;
    if (!row) return;
    const availableWidth = row.clientWidth;
    if (!forceMobile) {
      const requiredWidth = row.scrollWidth;
      requiredWidthRef.current = requiredWidth;
      if (requiredWidth > availableWidth + 1) {
        setForceMobile(true);
      }
      return;
    }
    const requiredWidth = requiredWidthRef.current;
    if (requiredWidth > 0 && availableWidth >= requiredWidth + 1) {
      setForceMobile(false);
    }
  }, [forceMobile]);

  const scheduleLayoutModeUpdate = useCallback(() => {
    if (typeof window === "undefined") return;
    if (layoutUpdateRafRef.current != null) {
      window.cancelAnimationFrame(layoutUpdateRafRef.current);
    }
    layoutUpdateRafRef.current = window.requestAnimationFrame(() => {
      layoutUpdateRafRef.current = null;
      updateLayoutMode();
    });
  }, [updateLayoutMode]);

  useLayoutEffect(() => {
    scheduleLayoutModeUpdate();
  }, [scheduleLayoutModeUpdate, language, currentUser, unreadCount, unreadTicketCount]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const row = headerRowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => scheduleLayoutModeUpdate());
    observer.observe(row);
    return () => {
      observer.disconnect();
      if (layoutUpdateRafRef.current != null) {
        window.cancelAnimationFrame(layoutUpdateRafRef.current);
        layoutUpdateRafRef.current = null;
      }
    };
  }, [scheduleLayoutModeUpdate]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (!languageMenuOpen) {
      const timeout = window.setTimeout(() => setLanguageMenuOffset({ x: 0, y: 0 }), 200);
      return () => window.clearTimeout(timeout);
    }
    const raf = window.requestAnimationFrame(() => {
      measureLanguageMenuOffset();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [languageMenuOpen, measureLanguageMenuOffset, uiScale]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (!appearanceMenuOpen) {
      const timeout = window.setTimeout(
        () => setAppearanceMenuOffset({ x: 0, y: 0 }),
        200
      );
      return () => window.clearTimeout(timeout);
    }
    const raf = window.requestAnimationFrame(() => {
      measureAppearanceMenuOffset();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [appearanceMenuOpen, measureAppearanceMenuOffset, uiScale]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (!profileMenuOpen) {
      const timeout = window.setTimeout(
        () => setProfileMenuOffset({ x: 0, y: 0 }),
        200
      );
      return () => window.clearTimeout(timeout);
    }
    const raf = window.requestAnimationFrame(() => {
      measureProfileMenuOffset();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [measureProfileMenuOffset, profileMenuOpen, uiScale]);

  return (
    <header
      className="border-b sticky top-0 z-50 shadow-sm header-muted"
      style={{
        backgroundColor: "var(--background)",
        borderColor: dividerStyle.borderColor,
      }}
    >
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div
          ref={headerRowRef}
          className="flex items-center justify-between gap-2"
        >
          {/* Logo und Titel */}
          <div
            className="flex items-center gap-2 md:gap-3 cursor-pointer flex-shrink-0"
            onClick={() => handlePrimaryNav("home")}
          >
            <div
              className="p-1.5 md:p-2 rounded-lg"
              style={brandIconWrapperStyle}
            >
              <Book className="w-5 h-5 md:w-6 md:h-6" style={brandIconStyle} />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-primary-heading text-base md:text-lg font-bold">
                {t("header.title")}
              </h1>
              <p
                className="text-xs hidden md:block font-medium"
                style={secondaryTextStyle}
              >
                {t("header.subtitle")}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav
            className={
              showDesktop ? "flex items-center gap-6 flex-shrink-0" : "hidden"
            }
          >
            <button
              onClick={() => handlePrimaryNav("home")}
              className="transition-colors font-semibold"
              onMouseEnter={() => setHoveredNav("home")}
              onMouseLeave={() => setHoveredNav(null)}
              style={navButtonStyle("home")}
            >
              {t("header.home")}
            </button>
            <button
              onClick={() => handlePrimaryNav("categories")}
              className="transition-colors flex items-center gap-2 font-semibold"
              onMouseEnter={() => setHoveredNav("categories")}
              onMouseLeave={() => setHoveredNav(null)}
              style={navButtonStyle("categories")}
            >
              <FolderTree className="w-4 h-4" style={iconStyle} />
              {t("header.categories")}
            </button>
            <button
              onClick={handleSearchNav}
              className="transition-colors flex items-center gap-2 font-semibold"
              onMouseEnter={() => setHoveredNav("search")}
              onMouseLeave={() => setHoveredNav(null)}
              style={navButtonStyle("search")}
            >
              <SearchIcon className="w-4 h-4" style={iconStyle} />
              {t("header.search")}
            </button>
            <button
              onClick={() => handlePrimaryNav("statistics")}
              className="transition-colors flex items-center gap-2 font-semibold"
              onMouseEnter={() => setHoveredNav("statistics")}
              onMouseLeave={() => setHoveredNav(null)}
              style={navButtonStyle("statistics")}
            >
              <BarChart3 className="w-4 h-4" style={iconStyle} />
              {t("header.statistics")}
            </button>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* Primary Actions - Desktop only */}
            {currentUser ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTargetNav("suggest-website")}
                  className={showDesktop ? "gap-2 flex font-semibold" : "hidden"}
                  style={primaryTextStyle}
                >
                  <Lightbulb className="w-4 h-4" style={iconStyle} />
                  {t("header.suggest")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTargetNav("manage-websites")}
                  className={showDesktop ? "gap-2 flex font-semibold" : "hidden"}
                  style={primaryTextStyle}
                >
                  <FileEdit className="w-4 h-4" style={iconStyle} />
                  {t("header.manage")}
                </Button>
                {currentUser.role === "admin" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleTargetNav("admin")}
                    className={
                      showDesktop ? "gap-2 flex font-semibold" : "hidden"
                    }
                    style={primaryTextStyle}
                  >
                    <Settings className="w-4 h-4" style={iconStyle} />
                    {t("header.administration")}
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTargetNav("suggest-website")}
                className={showDesktop ? "gap-2 flex font-semibold" : "hidden"}
                style={primaryTextStyle}
              >
                <Lightbulb className="w-4 h-4" style={iconStyle} />
                {t("header.suggest")}
              </Button>
            )}

            {/* Language Switcher - Hidden on small mobile */}
            <DropdownMenu open={languageMenuOpen} onOpenChange={setLanguageMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 hidden sm:flex px-2 font-semibold"
                  style={primaryTextStyle}
                  ref={languageMenuTriggerRef}
                >
                  <Globe className="w-4 h-4" style={iconStyle} />
                  <span
                    className="px-1.5 py-0.5 rounded text-xs font-semibold"
                    style={languageBadgeStyle}
                  >
                    {language === "de" ? "DE" : "EN"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                style={{
                  ...dropdownSurfaceStyle,
                  translate: `${languageMenuOffset.x}px ${languageMenuOffset.y}px`,
                }}
                data-language-menu
              >
                <DropdownMenuItem
                  onClick={() => setLanguage("de")}
                  className="gap-2"
                >
                  <span
                    className="px-2 py-0.5 rounded text-xs font-semibold"
                    style={languageBadgeStyle}
                  >
                    DE
                  </span>
                  <span className="font-semibold" style={primaryTextStyle}>
                    Deutsch{language === "de" && " ✓"}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage("en")}
                  className="gap-2"
                >
                  <span
                    className="px-2 py-0.5 rounded text-xs font-semibold"
                    style={languageBadgeStyle}
                  >
                    EN
                  </span>
                  <span className="font-semibold" style={primaryTextStyle}>
                    English{language === "en" && " ✓"}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Mode Selector + Settings */}
            {currentUser ? (
              <>
                <DropdownMenu open={appearanceMenuOpen} onOpenChange={setAppearanceMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0 w-9 h-9"
                      aria-label="Theme"
                      ref={appearanceMenuTriggerRef}
                    >
                      {themePreferences.mode === "sunrise-sunset" ? (
                        activeTheme.tone === "dark" ? (
                          <Sunset className="w-4 h-4 md:w-5 md:h-5" />
                        ) : (
                          <Sunrise className="w-4 h-4 md:w-5 md:h-5" />
                        )
                      ) : themePreferences.mode === "system" ? (
                        <Laptop className="w-4 h-4 md:w-5 md:h-5" />
                      ) : themePreferences.mode === "light" ? (
                        <Sun className="w-4 h-4 md:w-5 md:h-5" />
                      ) : themePreferences.mode === "dark" ? (
                        <Moon className="w-4 h-4 md:w-5 md:h-5" />
                      ) : (
                        <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    style={{
                      ...dropdownSurfaceStyle,
                      translate: `${appearanceMenuOffset.x}px ${appearanceMenuOffset.y}px`,
                    }}
                    data-appearance-menu
                  >
                    {[
                      {
                        value: "light",
                        label: t("appearance.modeLight"),
                        icon: <Sun className="w-4 h-4" />,
                      },
                      {
                        value: "dark",
                        label: t("appearance.modeDark"),
                        icon: <Moon className="w-4 h-4" />,
                      },
                      {
                        value: "system",
                        label: t("appearance.modeSystem"),
                        icon: <Laptop className="w-4 h-4" />,
                      },
                      {
                        value: "sunrise-sunset",
                        label: t("appearance.modeSun"),
                        icon:
                          activeTheme.tone === "dark" ? (
                            <Sunset className="w-4 h-4" />
                          ) : (
                            <Sunrise className="w-4 h-4" />
                          ),
                      },
                      {
                        value: "scheduled",
                        label: t("appearance.modeSchedule"),
                        icon: <Sparkles className="w-4 h-4" />,
                      },
                    ].map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        className="gap-2"
                        onClick={() => setThemeMode(option.value as any)}
                      >
                        {option.icon}
                        <span>{option.label}</span>
                        {themePreferences.mode === option.value && (
                          <span aria-hidden="true">✓</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleAppearanceNav}>
                      <Palette className="w-4 h-4" />
                      <span>{t("appearance.title")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0 w-9 h-9"
                aria-label={guestThemeLabel}
                title={guestThemeLabel}
                onClick={handleGuestThemeToggle}
              >
                {guestThemeIcon}
              </Button>
            )}

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0 w-9 h-9"
                onClick={decreaseUiScale}
                disabled={decreaseScaleDisabled}
                aria-label={t("header.decreaseUiScale")}
                title={t("header.decreaseUiScale")}
              >
                <Minus className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0 w-9 h-9"
                onClick={increaseUiScale}
                disabled={increaseScaleDisabled}
                aria-label={t("header.increaseUiScale")}
                title={t("header.increaseUiScale")}
              >
                <Plus className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
            </div>

            {currentUser ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMessagesNav}
                  className={showDesktop ? "relative rounded-full" : "hidden"}
                  aria-label={t("header.messages")}
                >
                  <span className="relative flex items-center justify-center overflow-visible">
                    <MessageSquare className="h-6 w-6" style={messageIconStyle} />
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleTicketsNav}
                  className={showDesktop ? "relative rounded-full" : "hidden"}
                  aria-label={t("header.tickets")}
                >
                  <span className="relative flex items-center justify-center overflow-visible">
                    <Bug className="h-6 w-6" style={ticketIconStyle} />
                  </span>
                </Button>

                {/* User Dropdown */}
                <DropdownMenu
                  open={profileMenuOpen}
                  onOpenChange={setProfileMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="gap-2 pl-1 pr-3 md:pl-2 md:pr-4 rounded-full"
                      ref={profileMenuTriggerRef}
                      style={primaryTextStyle}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
                        style={avatarStyle}
                      >
                        {profilePictureSrc ? (
                          <img
                            src={profilePictureSrc}
                            alt={currentUser.username}
                            className="w-full h-full object-cover"
                            onError={handleProfilePictureError}
                          />
                        ) : userInitials ? (
                      <span className="text-xs font-semibold" style={primaryTextStyle}>
                        {userInitials}
                      </span>
                    ) : (
                          <UserIcon className="w-4 h-4" style={secondaryTextStyle} />
                        )}
                      </div>
                      <span className="hidden md:inline font-semibold">
                        {currentUser.username}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="bottom"
                    sideOffset={6}
                    avoidCollisions={false}
                    align="end"
                    data-profile-menu
                    className="w-56"
                    style={{
                      translate: `${profileMenuOffset.x}px ${profileMenuOffset.y}px`,
                      backgroundColor: dropdownSurfaceStyle.backgroundColor,
                      borderColor: dropdownSurfaceStyle.borderColor,
                    }}
                  >
                    <div className="px-2 py-2">
                      <p className="truncate font-semibold" style={primaryTextStyle}>
                        {currentUser.username}
                      </p>
                      <p className="text-xs truncate" style={secondaryTextStyle}>
                        {currentUser.email}
                      </p>
                      <p className="text-xs mt-1" style={secondaryTextStyle}>
                        {currentUser.role === "admin"
                          ? t("account.administrator")
                          : t("account.registeredUser")}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleTargetNav("account")}
                    >
                      <UserIcon className="w-4 h-4 mr-2" style={iconStyle} />
                      {t("header.myAccount")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={onLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" style={iconStyle} />
                      {t("header.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex gap-1 md:gap-2">
                {/* On mobile: show Login, on desktop: show both Login and Register */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("login")}
                  className="sm:inline-flex font-semibold"
                  style={primaryTextStyle}
                >
                  {t("header.login")}
                </Button>
                <Button
                  onClick={() => navigate("register")}
                  className="text-sm px-3 md:px-4 hidden sm:inline-flex font-semibold"
                  size="sm"
                  onMouseEnter={() => setRegisterHovered(true)}
                  onMouseLeave={() => setRegisterHovered(false)}
                  style={registerButtonStyle}
                >
                  {t("header.register")}
                </Button>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={showDesktop ? "hidden" : "flex-shrink-0 w-9 h-9"}
                  aria-label={language === "de" ? "Menü öffnen" : "Open menu"}
                  title={language === "de" ? "Menü öffnen" : "Open menu"}
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                style={dropdownSurfaceStyle}
                className="overflow-y-auto max-h-screen"
                aria-describedby="mobile-nav-description"
              >
                <SheetHeader>
                  <SheetTitle className="text-primary-heading font-semibold">
                    {t("header.title")}
                  </SheetTitle>
                  <p id="mobile-nav-description" className="sr-only">
                    {t("header.subtitle")}
                  </p>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-6">
                  <Button
                    variant="ghost"
                    onClick={() => handlePrimaryNav("home")}
                    className="justify-start font-semibold"
                  >
                    {t("header.home")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handlePrimaryNav("categories")}
                    className="justify-start gap-2 font-semibold"
                  >
                    <FolderTree className="w-4 h-4" />
                    {t("header.categories")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleSearchNav}
                    className="justify-start gap-2 font-semibold"
                  >
                    <SearchIcon className="w-4 h-4" />
                    {t("header.search")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handlePrimaryNav("statistics")}
                    className="justify-start gap-2 font-semibold"
                  >
                    <BarChart3 className="w-4 h-4" />
                    {t("header.statistics")}
                  </Button>

                  <div className="border-t my-2" style={dividerStyle}></div>

                  <Button
                    variant="ghost"
                    onClick={() => handleTargetNav("suggest-website")}
                    className="justify-start gap-2 font-semibold"
                  >
                    <Lightbulb className="w-4 h-4" />
                    {t("header.suggest")}
                  </Button>

                  {currentUser && (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => handleTargetNav("manage-websites")}
                        className="justify-start gap-2 font-semibold"
                      >
                        <FileEdit className="w-4 h-4" />
                        {t("header.manage")}
                      </Button>
                      {currentUser.role === "admin" && (
                        <Button
                          variant="ghost"
                          onClick={() => handleTargetNav("admin")}
                          className="justify-start gap-2 font-semibold"
                        >
                          <Settings className="w-4 h-4" />
                          {t("header.administration")}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        onClick={handleMessagesNav}
                        className="justify-start gap-2 font-semibold"
                      >
                        <MessageSquare className="w-4 h-4" style={messageIconStyle} />
                        {t("header.messages")}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={handleTicketsNav}
                        className="justify-start gap-2 font-semibold"
                      >
                        <Bug className="w-4 h-4" style={ticketIconStyle} />
                        {t("header.tickets")}
                      </Button>
                    </>
                  )}

                  <div className="border-t my-2" style={dividerStyle}></div>

                  {/* Language Switcher in Mobile Menu */}
                  <div className="flex gap-2">
                    <Button
                      variant={language === "de" ? "default" : "outline"}
                      onClick={() => setLanguage("de")}
                      className="flex-1 font-semibold"
                      size="sm"
                    >
                      DE
                    </Button>
                    <Button
                      variant={language === "en" ? "default" : "outline"}
                      onClick={() => setLanguage("en")}
                      className="flex-1 font-semibold"
                      size="sm"
                    >
                      EN
                    </Button>
                  </div>
                  {!currentUser && (
                    <Button
                      variant="outline"
                      onClick={handleGuestThemeToggle}
                      className="w-full gap-2 font-semibold"
                      size="sm"
                    >
                      {guestThemeIcon}
                      {guestThemeLabel}
                    </Button>
                  )}

                  {!currentUser && (
                    <>
                      <div className="border-t my-2" style={dividerStyle}></div>
                      <Button
                        onClick={() => handleNavigate("login")}
                        className="w-full font-semibold"
                      >
                        {t("header.login")}
                      </Button>
                      <Button
                        onClick={() => handleNavigate("register")}
                        className="w-full font-semibold"
                        onMouseEnter={() => setRegisterHovered(true)}
                        onMouseLeave={() => setRegisterHovered(false)}
                        style={registerButtonStyle}
                      >
                        {t("header.register")}
                      </Button>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
